import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Table from "../../core/common/dataTable/index";
import { all_routes } from "../router/all_routes";
import {
  selectBranches,
  selectCurrentBranch,
} from "../../core/data/redux/slice/branchesSlice";
import {
  createRole,
  getRoleById,
  updateRole,
  getModulesWithPermissions,
  type CreateRoleData,
  type ModulePermission,
  type Module as ModuleType,
} from "../../service/roles-permission";
import { toast } from "react-toastify";

interface PermissionTableData {
  [key: string]: any;
  modules: string;
  allowAll: React.ReactNode;
  key: string;
}

interface PermissionState {
  [module: string]: {
    [permission: string]: boolean;
  };
}

const Permission = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const { id } = useParams();

  // Permissions are per-campus. `editBranch` is the campus whose grants are
  // shown/edited on this page; changing it reloads that campus's grants.
  const branches = useSelector(selectBranches);
  const currentBranch = useSelector(selectCurrentBranch);
  const [editBranch, setEditBranch] = useState<string>("");

  const [loading, setLoading] = useState(false);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [roleName, setRoleName] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [allFetchedModules, setAllFetchedModules] = useState<ModuleType[]>([]);
  const [permissions, setPermissions] = useState<PermissionState>({});
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });

  useEffect(() => {
    const initializeData = async () => {
      try {
        setModulesLoading(true);

        const response = await getModulesWithPermissions({
          page: 1,
          limit: 100,
        });

        const modules = response.modules || [];
        setAllFetchedModules(modules);

        setPagination({
          currentPage: 1,
          totalPages: Math.ceil(modules.length / 10),
          total: modules.length,
          limit: 10,
          hasNextPage: 1 < Math.ceil(modules.length / 10),
          hasPrevPage: false,
        });

        if (modules.length > 0) {
          const initialPermissions = initializePermissionState(modules);
          setPermissions(initialPermissions);

          if (id) {
            await loadRoleData(id, modules, initialPermissions, editBranch || currentBranch?._id);
          } else {
            setIsEditMode(false);
          }
        }
      } catch (error) {
        toast.error("Error loading data");
      } finally {
        setModulesLoading(false);
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Default the edited campus to the currently-selected one.
  useEffect(() => {
    if (!editBranch && currentBranch?._id) setEditBranch(currentBranch._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBranch?._id]);

  // Switch the campus whose grants are being edited → reload that campus's
  // grants (each campus can have different rights for the same role).
  const handleBranchChange = async (branchId: string) => {
    setEditBranch(branchId);
    if (id && allFetchedModules.length) {
      const initial = initializePermissionState(allFetchedModules);
      setPermissions(initial);
      await loadRoleData(id, allFetchedModules, initial, branchId);
    }
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
      limit: pageSize,
    }));
  };

  const initializePermissionState = (
    modules: ModuleType[]
  ): PermissionState => {
    const state: PermissionState = {};

    if (!modules || !Array.isArray(modules)) {
      return state;
    }

    modules.forEach((module) => {
      if (module.name && Array.isArray(module.actions)) {
        state[module.name] = {};
        module.actions.forEach((action: string) => {
          state[module.name][action] = false;
        });
      }
    });
    return state;
  };

  const loadRoleData = async (
    roleId: string,
    modules: ModuleType[],
    initialPermissions: PermissionState,
    branch?: string
  ) => {
    setLoading(true);
    try {
      const response = await getRoleById(roleId, branch || undefined);
      const roleData = response.data;

      if (roleData && roleData.name) {
        setRoleName(roleData.name);
        setIsEditMode(true);

        const rolePermissions = { ...initialPermissions };

        if (roleData.permissions && Array.isArray(roleData.permissions)) {
          roleData.permissions.forEach((permission: any) => {
            if (
              permission.grantedActions &&
              Array.isArray(permission.grantedActions)
            ) {
              let targetModule: ModuleType | undefined;

              if (permission.module && typeof permission.module === "string") {
                targetModule = modules.find(
                  (mod) => mod._id === permission.module
                );
              }

              if (
                !targetModule &&
                permission.module &&
                typeof permission.module === "object"
              ) {
                const moduleId = permission.module._id;
                const moduleName = permission.module.name;

                if (moduleId) {
                  targetModule = modules.find((mod) => mod._id === moduleId);
                }
                if (!targetModule && moduleName) {
                  targetModule = modules.find((mod) => mod.name === moduleName);
                }
              }

              if (targetModule && rolePermissions[targetModule.name]) {
                permission.grantedActions.forEach((action: string) => {
                  if (!action || typeof action !== "string") return;

                  const normalizedAction = action.trim().toLowerCase();
                  const availableActions = Object.keys(
                    rolePermissions[targetModule!.name]
                  );
                  const exactMatch = availableActions.find(
                    (availableAction) =>
                      availableAction.trim().toLowerCase() === normalizedAction
                  );

                  if (exactMatch) {
                    rolePermissions[targetModule!.name][exactMatch] = true;
                  }
                });
              }
            }
          });
        }

        setPermissions(rolePermissions);
      }
    } catch (error) {
      toast.error("Error loading role data");
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (
    module: string,
    permission: string,
    checked: boolean
  ) => {
    setPermissions((prev) => ({
      ...prev,
      [module]: {
        ...prev[module],
        [permission]: checked,
      },
    }));
  };

  const handleSelectAllModule = (module: string, checked: boolean) => {
    setPermissions((prev) => {
      const modulePermissions = prev[module];
      if (!modulePermissions) return prev;

      const updatedModule = { ...modulePermissions };
      Object.keys(updatedModule).forEach((permission) => {
        updatedModule[permission] = checked;
      });
      return {
        ...prev,
        [module]: updatedModule,
      };
    });
  };

  const isModuleAllSelected = (module: string): boolean => {
    const modulePermissions = permissions[module];
    if (!modulePermissions) return false;

    return Object.values(modulePermissions).every(Boolean);
  };

  const permissionsToModulesArray = (
    permState: PermissionState
  ): ModulePermission[] => {
    const modules: ModulePermission[] = [];

    Object.keys(permState).forEach((moduleName) => {
      const modulePermissions = permState[moduleName];
      if (modulePermissions) {
        const selectedPermissions = Object.keys(modulePermissions).filter(
          (permission) => modulePermissions[permission]
        );

        if (selectedPermissions.length > 0) {
          const moduleData = allFetchedModules.find(
            (mod) => mod.name === moduleName
          );
          if (moduleData) {
            modules.push({
              module: moduleData._id,
              actions: selectedPermissions,
            });
          }
        }
      }
    });

    return modules;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!roleName.trim()) {
      toast.error("Please enter role name");
      return;
    }

    const selectedModules = permissionsToModulesArray(permissions);

    if (selectedModules.length === 0) {
      toast.error("Please select at least one permission");
      return;
    }

    setLoading(true);
    try {
      if (isEditMode && id) {
        await updateRole(id, {
          name: roleName,
          modules: selectedModules,
          branch: editBranch || currentBranch?._id,
        });
        toast.success(
          `Role updated for ${
            branches.find((b: any) => b._id === (editBranch || currentBranch?._id))?.name || "this campus"
          }!`
        );
      } else {
        const roleData: CreateRoleData = {
          name: roleName,
          modules: selectedModules,
        };
        await createRole(roleData);
        toast.success("Role added successfully!");
      }

      navigate(routes.rolesPermissions);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Error saving role. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const indexOfLastItem = pagination.currentPage * pagination.limit;
  const indexOfFirstItem = indexOfLastItem - pagination.limit;
  const currentModules = allFetchedModules.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const tableData: PermissionTableData[] = currentModules.map(
    (moduleData, index) => {
      const rowData: PermissionTableData = {
        key: moduleData._id || `module-${index}`,
        modules:
          moduleData.name.charAt(0).toUpperCase() + moduleData.name.slice(1),
        allowAll: (
          <label className="checkboxs" key={`${moduleData.name}-allowAll`}>
            <input
              type="checkbox"
              checked={isModuleAllSelected(moduleData.name)}
              onChange={(e) =>
                handleSelectAllModule(moduleData.name, e.target.checked)
              }
            />
            <span className="checkmarks" />
          </label>
        ),
      };

      if (Array.isArray(moduleData.actions)) {
        moduleData.actions.forEach((action: string) => {
          rowData[action] = (
            <label className="checkboxs" key={`${moduleData.name}-${action}`}>
              <input
                type="checkbox"
                checked={permissions[moduleData.name]?.[action] || false}
                onChange={(e) => {
                  e.stopPropagation();
                  handlePermissionChange(
                    moduleData.name,
                    action,
                    e.target.checked
                  );
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <span className="checkmarks" />
            </label>
          );
        });
      }

      return rowData;
    }
  );

  const getTableColumns = () => {
    if (allFetchedModules.length === 0) return [];

    const allPermissions = new Set<string>();
    allFetchedModules.forEach((module) => {
      if (Array.isArray(module.actions)) {
        module.actions.forEach((action) => {
          allPermissions.add(action);
        });
      }
    });

    const permissionColumns = Array.from(allPermissions).map((permission) => ({
      title: permission.charAt(0).toUpperCase() + permission.slice(1),
      dataIndex: permission,
      key: permission,
      render: (text: string, record: PermissionTableData) => record[permission],
      sorter: (a: PermissionTableData, b: PermissionTableData) => {
        const aVal = a[permission];
        const bVal = b[permission];
        return String(aVal).localeCompare(String(bVal));
      },
    }));

    return [
      {
        title: "Modules",
        dataIndex: "modules",
        key: "modules",
        render: (text: string, record: PermissionTableData) => {
          const formattedName = text
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
          return formattedName;
        },
        sorter: (a: PermissionTableData, b: PermissionTableData) =>
          a.modules.localeCompare(b.modules),
      },
      ...permissionColumns,
      {
        title: "Allow All",
        dataIndex: "allowAll",
        key: "allowAll",
        render: (text: string, record: PermissionTableData) => record.allowAll,
        sorter: (a: PermissionTableData, b: PermissionTableData) => {
          return a.modules.localeCompare(b.modules);
        },
      },
    ];
  };

  if (modulesLoading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "200px" }}
          >
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                {isEditMode ? "Edit Role" : "Add New Role"}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.rolesPermissions}>
                      Roles & Permissions
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEditMode ? "Edit Role" : "Add Role"}
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Role Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={roleName}
                        onChange={(e) => setRoleName(e.target.value)}
                        required
                        placeholder="Enter Role Name"
                      />
                    </div>
                  </div>
                  {isEditMode && branches.length > 1 && (
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Campus{" "}
                          <span className="text-muted small">
                            (permissions below apply to this campus)
                          </span>
                        </label>
                        <select
                          className="form-control"
                          value={editBranch || currentBranch?._id || ""}
                          onChange={(e) => handleBranchChange(e.target.value)}
                        >
                          {branches.map((b: any) => (
                            <option key={b._id} value={b._id}>
                              {b.campusNo ? `${b.campusNo} — ` : ""}
                              {b.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Select Permissions</h4>
              <div className="d-flex align-items-center flex-wrap"></div>
            </div>
            <div className="card-body p-0 py-3">
              {allFetchedModules.length > 0 ? (
                <Table
                  columns={getTableColumns()}
                  dataSource={tableData}
                  pagination={{
                    currentPage: pagination.currentPage,
                    total: allFetchedModules.length,
                    totalPages: Math.ceil(
                      allFetchedModules.length / pagination.limit
                    ),
                    limit: pagination.limit,
                    hasNextPage:
                      pagination.currentPage <
                      Math.ceil(allFetchedModules.length / pagination.limit),
                    hasPrevPage: pagination.currentPage > 1,
                    onChange: handlePageChange,
                  }}
                />
              ) : (
                <div className="text-center py-4">
                  <p>No modules found</p>
                </div>
              )}
            </div>

            <div className="card-footer bg-transparent border-0 py-3">
              <div className="d-flex justify-content-end align-items-center">
                <button
                  className="btn btn-light me-2"
                  onClick={() => navigate(routes.rolesPermissions)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading
                    ? isEditMode
                      ? "Updating..."
                      : "Submitting..."
                    : isEditMode
                    ? "Update"
                    : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Permission;
